import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { Popup } from 'devextreme-react/popup';
import { DataGrid, Column, SearchPanel, Paging, FilterRow, Selection, KeyboardNavigation, Button as Butt } from 'devextreme-react/data-grid';
import { Button, LoadIndicator, LoadPanel, TextBox, Toolbar } from 'devextreme-react';
import { Item } from 'devextreme-react/toolbar';
import { Tooltip } from 'devextreme-react/tooltip';
import { newToast, ModalConfirmation } from '../../../components';
import { useScreenSize } from '../../../utils/media-query';
import { useApiExternalRemisions } from '../../../api/api.externalRemisions';
import { useApiConfiguration } from '../../../api/api.configuration';
import { FacturacionTarifas } from '../../../types/types.facturacion';
import { SysI_RemisionesExternasPacientes, SysI_RemisionesExternasPacientesDetalle } from '../../../types/types.externalRemissions';
import { useApiPatient } from '../../../api/api.patient';
import { useAppSelector, useAppDispatch } from '../../../contexts/hooks';
import { setCurrentPatientToTransfer } from '../../../contexts/slices/orders';
import { TypeTableFilter } from '../../../types/types.system';
import moment from 'moment';

import './ModalViewExternalRemision.scss';
import { tracerModule, save, resGridNotesCheck } from '../../../images/icons';

interface ModalViewExternalRemisionProps {
  visible: boolean;
  remisionId: number;
  contractId?: number | null;
  labName?: string;
  remisionEstado?: boolean;
  remisionNota?: string | null;
  onClose: () => void;
}

export const ModalViewExternalRemision: React.FC<ModalViewExternalRemisionProps> = ({ visible, remisionId, contractId, labName, remisionEstado, remisionNota, onClose }) => {
  const [loadingPatients, setLoadingPatients] = useState(false);
  const [loadingDetails, setLoadingDetails] = useState(false);
  const [patients, setPatients] = useState<SysI_RemisionesExternasPacientes[]>([]);
  const [details, setDetails] = useState<SysI_RemisionesExternasPacientesDetalle[]>([]);
  const [selectedPatient, setSelectedPatient] = useState<SysI_RemisionesExternasPacientes | null>(null);

  // Estado para tarifas del contrato y edición de estudio
  const [lstTarifas, setLstTarifas] = useState<FacturacionTarifas[]>([]);
  const [editingDetail, setEditingDetail] = useState<SysI_RemisionesExternasPacientesDetalle | null>(null);
  const [editSearchText, setEditSearchText] = useState('');
  const [savingDetail, setSavingDetail] = useState(false);
  const [confirmDeletePatientVisible, setConfirmDeletePatientVisible] = useState(false);
  const [confirmDeleteDetailVisible, setConfirmDeleteDetailVisible] = useState(false);
  const pendingDeletePatient = useRef<SysI_RemisionesExternasPacientes | null>(null);
  const pendingDeleteDetail = useRef<SysI_RemisionesExternasPacientesDetalle | null>(null);

  // Monitoreo state
  const [monitoreoVisible, setMonitoreoVisible] = useState(false);
  const [monitoreoData, setMonitoreoData] = useState<any[]>([]);
  const [loadingMonitoreo, setLoadingMonitoreo] = useState(false);

  const { isSmall } = useScreenSize();
  const { getExternalRemisionPatients, getExternalRemisionPatientsDetails, updateExternalRemisionPatientsDetails, updateExternalRemisionPatients, deleteRemisionExternaPatient, deleteRemisionExternaPatientDetail, getMonitoreoByIdRm, importRemisionExterna } = useApiExternalRemisions();
  const { GetTarifasByIdContratoWithStudies } = useApiConfiguration();

  const patientsGridRef = useRef<DataGrid>(null);
  const detailsGridRef = useRef<DataGrid>(null);
  const loadedRemisionId = useRef<number | null>(null);

  // Patient linking state
  const { findUser } = useApiPatient();
  const dispatch = useAppDispatch();
  const currentPatientToTransfer = useAppSelector((x) => x.globalOrders.CurrentPatientToTransfer);
  const contextPatientState = useAppSelector((x) => x.globalOrders.CurrentOrder.ContextPatient);
  const modalPatientVisible = useAppSelector((x) => x.globalOrders.CurrentOrder.ModalPatient);
  const Prm_ExternalRem_I = useAppSelector((x) => (x.userInfo.Permissions ? x.userInfo.Permissions.Prm_ExternalRem_I : false));
  const [importingRemision, setImportingRemision] = useState(false);
  const [confirmImportVisible, setConfirmImportVisible] = useState(false);
  const [linkingPatient, setLinkingPatient] = useState<SysI_RemisionesExternasPacientes | null>(null);
  const [searchDocument, setSearchDocument] = useState('');
  const [foundPatient, setFoundPatient] = useState<any>(null);
  const [searchLoading, setSearchLoading] = useState(false);
  const [linkLoading, setLinkLoading] = useState(false);

  const loadPatients = useCallback(async (idRm: number) => {
    setLoadingPatients(true);
    try {
      const result = await getExternalRemisionPatients(idRm);
      setPatients(result && Array.isArray(result) ? result : []);
    } catch {
      newToast('ERROR', 'Error al cargar los pacientes', 4);
      setPatients([]);
    } finally {
      setLoadingPatients(false);
    }
  }, []);

  const loadDetails = useCallback(async (idRmPc: number) => {
    setLoadingDetails(true);
    try {
      const result = await getExternalRemisionPatientsDetails(idRmPc);
      setDetails(result && Array.isArray(result) ? result : []);
    } catch {
      newToast('ERROR', 'Error al cargar los estudios', 4);
      setDetails([]);
    } finally {
      setLoadingDetails(false);
    }
  }, []);

  useEffect(() => {
    if (visible && remisionId) {
      if (loadedRemisionId.current !== remisionId) {
        loadPatients(remisionId);
        loadedRemisionId.current = remisionId;
        setSelectedPatient(null);
        setDetails([]);
      }
    } else if (!visible) {
      setPatients([]);
      setDetails([]);
      setSelectedPatient(null);
      setLinkingPatient(null);
      setFoundPatient(null);
      setSearchDocument('');
      loadedRemisionId.current = null;
    }
  }, [visible, remisionId, loadPatients]);

  // Cargar tarifas del contrato
  useEffect(() => {
    if (visible && contractId && contractId > 0) {
      GetTarifasByIdContratoWithStudies(contractId).then((res) => {
        setLstTarifas(res && Array.isArray(res) ? res : []);
      });
    }
  }, [visible, contractId]);

  // Effect: when a patient is created via the global modal, link it to the remision
  useEffect(() => {
    if (!visible || !currentPatientToTransfer || contextPatientState !== 'externalRemisions' || !linkingPatient || modalPatientVisible) return;
    const pcId = currentPatientToTransfer.Pc_Id;
    const repId = linkingPatient.Rep_Id;
    dispatch(setCurrentPatientToTransfer(undefined));
    setLinkLoading(true);
    updateExternalRemisionPatients(pcId, repId)
      .then((result) => {
        if (result) {
          loadPatients(remisionId);
          setLinkingPatient(null);
          setSearchDocument('');
          setFoundPatient(null);
        }
      })
      .finally(() => setLinkLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentPatientToTransfer, modalPatientVisible]);

  const handleOpenLinkPatient = useCallback(
    (patient: SysI_RemisionesExternasPacientes) => {
      setLinkingPatient(patient);
      setSearchDocument(patient.Rep_Documento || '');
      setFoundPatient(null);
      dispatch(setCurrentPatientToTransfer(undefined));
    },
    [dispatch]
  );

  const handleCloseLinkPatient = useCallback(() => {
    setLinkingPatient(null);
    setSearchDocument('');
    setFoundPatient(null);
  }, []);

  const handleSearchPatient = useCallback(
    async (e?: React.FormEvent) => {
      if (e) e.preventDefault();
      if (!searchDocument.trim()) return;
      setFoundPatient(null);
      const result = await findUser([{ TypeFilter: TypeTableFilter.Equal, Field: 'Pc_Identificacion', Value: searchDocument.trim() }], setSearchLoading, false, 'externalRemisions');
      if (result) {
        setFoundPatient(result);
      }
    },
    [searchDocument, findUser]
  );

  const handleLinkFoundPatient = useCallback(async () => {
    if (!foundPatient?.Paciente?.Pc_Id || !linkingPatient) return;
    setLinkLoading(true);
    try {
      const result = await updateExternalRemisionPatients(foundPatient.Paciente.Pc_Id, linkingPatient.Rep_Id);
      if (result) {
        loadPatients(remisionId);
        handleCloseLinkPatient();
      }
    } finally {
      setLinkLoading(false);
    }
  }, [foundPatient, linkingPatient, updateExternalRemisionPatients, remisionId, loadPatients, handleCloseLinkPatient]);

  const handleOpenMonitoreo = useCallback(async () => {
    if (!remisionId) return;
    setMonitoreoVisible(true);
    setLoadingMonitoreo(true);
    try {
      const result = await getMonitoreoByIdRm(remisionId);
      setMonitoreoData(result && Array.isArray(result) ? result : []);
    } catch {
      newToast('ERROR', 'Error al cargar el monitoreo', 4);
      setMonitoreoData([]);
    } finally {
      setLoadingMonitoreo(false);
    }
  }, [remisionId, getMonitoreoByIdRm]);

  const handlePatientClick = useCallback(
    (e: any) => {
      if (e.data) {
        const patient = e.data as SysI_RemisionesExternasPacientes;
        setSelectedPatient(patient);
        loadDetails(patient.Rep_Id);
      }
    },
    [loadDetails]
  );

  const handleConfirmImport = useCallback(async () => {
    setConfirmImportVisible(false);
    if (!remisionId) return;

    if (remisionEstado === true) {
      newToast('WARNING', 'Esta remisión ya fue procesada', 4);
      return;
    }

    setImportingRemision(true);
    try {
      const result = await importRemisionExterna(remisionId);
      if (result) {
        onClose();
      }
    } catch {
      newToast('ERROR', 'Error al importar la remisión', 4);
    } finally {
      setImportingRemision(false);
    }
  }, [remisionId, importRemisionExterna, onClose]);

  const handleClose = useCallback(() => onClose(), [onClose]);

  // Handlers de eliminación
  const handleDeletePatient = useCallback((e: any) => {
    const patient = e.row?.data as SysI_RemisionesExternasPacientes;
    if (!patient?.Rep_Id) return;
    pendingDeletePatient.current = patient;
    setConfirmDeletePatientVisible(true);
  }, []);

  const handleConfirmDeletePatient = useCallback(async () => {
    setConfirmDeletePatientVisible(false);
    const patient = pendingDeletePatient.current;
    if (!patient?.Rep_Id) return;
    const result = await deleteRemisionExternaPatient(patient.Rep_Id);
    if (result) {
      setPatients((prev) => prev.filter((p) => p.Rep_Id !== patient.Rep_Id));
      if (selectedPatient?.Rep_Id === patient.Rep_Id) {
        setSelectedPatient(null);
        setDetails([]);
      }
    }
    pendingDeletePatient.current = null;
  }, [deleteRemisionExternaPatient, selectedPatient]);

  const handleCancelDeletePatient = useCallback(() => {
    setConfirmDeletePatientVisible(false);
    pendingDeletePatient.current = null;
  }, []);

  const handleDeleteDetail = useCallback((e: any) => {
    const detail = e.row?.data as SysI_RemisionesExternasPacientesDetalle;
    if (!detail?.Repd_Id) return;
    pendingDeleteDetail.current = detail;
    setConfirmDeleteDetailVisible(true);
  }, []);

  const handleConfirmDeleteDetail = useCallback(async () => {
    setConfirmDeleteDetailVisible(false);
    const detail = pendingDeleteDetail.current;
    if (!detail?.Repd_Id) return;
    const result = await deleteRemisionExternaPatientDetail(detail.Repd_Id);
    if (result) {
      setDetails((prev) => prev.filter((d) => d.Repd_Id !== detail.Repd_Id));
    }
    pendingDeleteDetail.current = null;
  }, [deleteRemisionExternaPatientDetail]);

  const handleCancelDeleteDetail = useCallback(() => {
    setConfirmDeleteDetailVisible(false);
    pendingDeleteDetail.current = null;
  }, []);

  // --- Edición de estudio local ---
  const handleEditStudy = useCallback((detail: SysI_RemisionesExternasPacientesDetalle) => {
    setEditingDetail(detail);
    setEditSearchText('');
  }, []);

  const handleCloseEdit = useCallback(() => {
    setEditingDetail(null);
    setEditSearchText('');
  }, []);

  // Filtrado de tarifas para el popup de edición
  const filteredTarifas = useMemo(() => {
    if (!editSearchText || editSearchText.length < 2) return lstTarifas.slice(0, 50);
    const search = editSearchText.toLowerCase();
    return lstTarifas.filter((t) => t.Estudio?.Es_Nombre?.toLowerCase().includes(search) || t.Estudio?.Es_Codigo?.toLowerCase().includes(search)).slice(0, 50);
  }, [lstTarifas, editSearchText]);

  // Seleccionar un estudio del contrato y guardar
  const handleSelectTarifa = useCallback(
    async (tarifa: FacturacionTarifas) => {
      if (!editingDetail) return;
      setSavingDetail(true);
      try {
        const updatedDetail: SysI_RemisionesExternasPacientesDetalle = {
          ...editingDetail,
          Repd_Es_Id: tarifa.Tf_Es_Id,
          Repd_Tf_Id: tarifa.Tf_Id,
        };
        const result = await updateExternalRemisionPatientsDetails(updatedDetail, editingDetail.Repd_Id!);
        if (result) {
          // Actualizar el detalle en el state local
          setDetails((prev) => prev.map((d) => (d.Repd_Id === editingDetail.Repd_Id ? { ...d, Repd_Es_Id: tarifa.Tf_Es_Id, Repd_Tf_Id: tarifa.Tf_Id, Estudio: tarifa.Estudio || null, Tarifa: tarifa as any, Repd_Alertas: null } : d)));
          handleCloseEdit();
        }
      } catch {
        newToast('ERROR', 'Error al actualizar el estudio', 4);
      } finally {
        setSavingDetail(false);
      }
    },
    [editingDetail, updateExternalRemisionPatientsDetails, handleCloseEdit]
  );

  // --- Cell renders (pacientes) ---

  // Orden + icono alerta si hay Rep_Alertas
  const renderOrden = useCallback((cellData: any) => {
    const alertas = cellData.data?.Rep_Alertas;
    return (
      <span>
        {cellData.value}
        {alertas && (
          <span id={`alerta-pac-${cellData.data.Rep_Id}`} className='orden-alerta'>
            {' '}
            ⚠
          </span>
        )}
      </span>
    );
  }, []);

  // Paciente compacto: nombre en una línea
  const renderPaciente = useCallback((cellData: any) => {
    const p = cellData.data?.Paciente;
    if (!p) return <span className='text-muted'>Sin vincular</span>;
    const nombre = `${p.Pc_Nombre || ''} ${p.Pc_Apellido1 || ''}`.trim();
    return (
      <div className='cell-paciente'>
        <span className='cell-paciente-nombre'>{nombre || '—'}</span>
        <span className='cell-paciente-doc'>DOC: {cellData.data.Rep_Documento}</span>
      </div>
    );
  }, []);

  // Estado vinculación mini
  const renderVinculacion = useCallback(
    (cellData: any) => {
      const ok = !!cellData.data?.Rep_Pc_Id;
      if (ok) return <span className='chip chip-ok'>Vinculado</span>;
      return (
        <div className='vinculacion-cell'>
          <span className='chip chip-warn'>Sin vincular</span>
          <button
            className='btn-link-patient'
            title='Vincular paciente'
            onClick={(e) => {
              e.stopPropagation();
              handleOpenLinkPatient(cellData.data);
            }}>
            🔗
          </button>
        </div>
      );
    },
    [handleOpenLinkPatient]
  );

  // --- Cell renders (estudios) ---

  // Estudio externo: código + nombre juntos
  const renderEstudioExterno = useCallback((cellData: any) => {
    const d = cellData.data;
    return (
      <div className='cell-estudio'>
        <span className='cell-estudio-nombre'>{d?.Repd_NombreEstudio || '—'}</span>
        <span className='cell-estudio-codigo'>
          Cod: {d?.Repd_CodigoEstudio || '—'} | Id: {d?.Repd_IdEstudio || '—'}
        </span>
      </div>
    );
  }, []);

  // Estudio local: nombre + código juntos + botón editar
  const renderEstudioLocal = useCallback(
    (cellData: any) => {
      const e = cellData.data?.Estudio;
      const detail = cellData.data as SysI_RemisionesExternasPacientesDetalle;
      const hasContract = contractId && contractId > 0;      
      return (
        <div className='cell-estudio-local-row'>
          <div className='cell-estudio'>
            {e?.Es_Nombre ? (
              <>
                <span className='cell-estudio-nombre'>{e.Es_Nombre}</span>
                {e.Es_Codigo && <span className='cell-estudio-codigo'>Cod: {e.Es_Codigo}</span>}
              </>
            ) : (
              <span className='text-muted'>No homologado</span>
            )}
          </div>
          {hasContract && (
            <button className='btn-edit-study' title='Cambiar estudio local' onClick={() => handleEditStudy(detail)}>
              ✎
            </button>
          )}
        </div>
      );
    },
    [contractId, handleEditStudy]
  );

  // Homologación mini chip
  const renderHomologacion = useCallback((cellData: any) => {
    const ok = !!cellData.data?.Repd_Es_Id;
    return <span className={`chip ${ok ? 'chip-ok' : 'chip-warn'}`}>{ok ? 'Homologado' : 'No homologado'}</span>;
  }, []);

  // Tarifa
  const renderTarifa = useCallback((cellData: any) => {
    const t = cellData.data?.Tarifa;
    if (!t) return <span className='text-muted'>—</span>;
    return <span className='cell-tarifa'>${t.Tf_Valor?.toLocaleString('es-CO') || '0'}</span>;
  }, []);

  // Indicadores del detalle: urgente, alerta y nota. Solo se muestran los que aplican.
  const renderIndicadores = useCallback((cellData: any) => {
    const d = cellData.data;
    const urgente = !!d?.Repd_Urgente;
    const alerta = !!d?.Repd_Alertas;
    const nota = !!(d?.Repd_Notas && String(d.Repd_Notas).trim());

    if (!urgente && !alerta && !nota) return <span className='text-muted'>—</span>;

    return (
      <div className='cell-indicadores'>
        {urgente && (
          <span id={`urg-det-${d.Repd_Id}`} className='ind-urgente' title='Urgente'>
            🚩
          </span>
        )}
        {alerta && (
          <span id={`alerta-det-${d.Repd_Id}`} className='orden-alerta'>
            ⚠
          </span>
        )}
        {nota && (
          <img id={`nota-det-${d.Repd_Id}`} className='ind-nota' src={resGridNotesCheck} alt='Nota' style={{ width: 18, height: 18 }} />
        )}
      </div>
    );
  }, []);

  if (!visible) return null;

  return (
    <Popup visible={visible} onHiding={handleClose} dragEnabled={false} hideOnOutsideClick={false} showCloseButton={true} showTitle={true} title={`Remisión Externa #${remisionId} — ${labName || ''}`} fullScreen={isSmall} width={isSmall ? '100%' : '95%'} height={isSmall ? '100%' : '90%'}>
      <div style={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
        {/* Nota de la remisión (si viene) */}
        {remisionNota && String(remisionNota).trim() && (
          <div className='ext-rem-nota-banner'>
            <img src={resGridNotesCheck} alt='Nota' style={{ width: 18, height: 18, flexShrink: 0 }} />
            <span>{remisionNota}</span>
          </div>
        )}

        {/* Paneles divididos 30 / 70 */}
        <div style={{ flex: 1, display: 'flex', flexDirection: isSmall ? 'column' : 'row', minHeight: 0 }}>
          {/* Panel izquierdo: Pacientes (30%) */}
          <div style={{ width: isSmall ? '100%' : '30%', display: 'flex', flexDirection: 'column', borderRight: isSmall ? 'none' : '1px solid #ccc' }}>
            <div className='ext-rem-toolbar'>
              <Toolbar>
                <Item location='before' locateInMenu='never'>
                  <Button id='btnMonitoreoRem' icon={tracerModule} stylingMode='text' onClick={handleOpenMonitoreo} />
                  <Tooltip target='#btnMonitoreoRem' showEvent='mouseenter' hideEvent='mouseleave' position='top'>
                    Trazabilidad
                  </Tooltip>
                </Item>
                <Item location='before' locateInMenu='never'>
                  <span className='toolbar-title'>Pacientes</span>
                </Item>
                <Item location='after' locateInMenu='never'>
                  <div className='ext-rem-badge'>{patients.length}</div>
                </Item>

                <Item location='after' locateInMenu='never' visible={Prm_ExternalRem_I && !remisionEstado}>
                  <Button id='btnConfirmRemision' icon={save} stylingMode='text' onClick={() => setConfirmImportVisible(true)} disabled={patients.length === 0 || importingRemision} />
                  <Tooltip target='#btnConfirmRemision' showEvent='mouseenter' hideEvent='mouseleave' position='top'>
                    Confirmar remisión
                  </Tooltip>
                </Item>
              </Toolbar>
            </div>

            {loadingPatients ? (
              <div className='panel-loading'>
                <LoadIndicator visible={true} height={30} width={30} />
                <span>Cargando...</span>
              </div>
            ) : (
              <DataGrid ref={patientsGridRef} dataSource={patients} keyExpr='Rep_Id' showBorders={true} rowAlternationEnabled={true} hoverStateEnabled={true} height='100%' showColumnLines={true} onRowClick={handlePatientClick}>
                <Selection mode='single' />
                <KeyboardNavigation editOnKeyPress={false} enterKeyAction='moveFocus' enterKeyDirection='row' />
                <SearchPanel visible={true} placeholder='Buscar...' width={150} />
                <Paging enabled={true} pageSize={30} />

                <Column dataField='Rep_Orden' caption='Orden' cellRender={renderOrden} alignment='center' width={110} />
                <Column
                  dataField='Rep_Documento'
                  caption='Paciente'
                  cellRender={renderPaciente}
                  calculateCellValue={(rowData: any) => {
                    const p = rowData?.Paciente;
                    const nombre = p ? `${p.Pc_Nombre || ''} ${p.Pc_Apellido1 || ''} ${p.Pc_Apellido2 || ''}`.trim() : '';
                    return `${nombre} ${rowData?.Rep_Documento || ''}`.trim();
                  }}
                />
                <Column dataField='Rep_Pc_Id' caption='Vinculación' cellRender={renderVinculacion} alignment='center' width={120} />
                <Column type='buttons' width={40} caption=''>
                  <Butt icon='trash' hint='Eliminar paciente' onClick={handleDeletePatient} />
                </Column>
              </DataGrid>
            )}
            {/* Tooltips alertas pacientes */}
            {patients
              .filter((p) => p.Rep_Alertas)
              .map((p) => (
                <Tooltip key={`tip-pac-${p.Rep_Id}`} target={`#alerta-pac-${p.Rep_Id}`} showEvent='mouseenter' hideEvent='mouseleave' position='right'>
                  <div style={{ padding: '4px', whiteSpace: 'pre-line', maxWidth: 280 }}>{p.Rep_Alertas}</div>
                </Tooltip>
              ))}
          </div>

          {/* Panel derecho: Estudios (70%) */}
          <div style={{ width: isSmall ? '100%' : '70%', display: 'flex', flexDirection: 'column' }}>
            <div className='panel-headerExternalRemision panel-header-details'>
              <h4>
                Estudios
                {selectedPatient && (
                  <span className='panel-subtitle'>
                    {' '}
                    — {selectedPatient.Rep_Orden} | {selectedPatient.Rep_Documento}
                  </span>
                )}
              </h4>
              {details.length > 0 && <div className='ext-rem-badge'>{details.length}</div>}
            </div>

            {!selectedPatient ? (
              <div className='panel-empty'>
                <h5>Seleccione un paciente</h5>
                <p>Haga clic en un paciente para ver sus estudios.</p>
              </div>
            ) : loadingDetails ? (
              <div className='panel-loading'>
                <LoadIndicator visible={true} height={30} width={30} />
                <span>Cargando estudios...</span>
              </div>
            ) : (
              <DataGrid ref={detailsGridRef} dataSource={details} keyExpr='Repd_Id' showBorders={true} rowAlternationEnabled={true} hoverStateEnabled={true} height='100%' showColumnLines={true}>
                <SearchPanel visible={true} placeholder='Buscar...' width={180} />
                <Paging enabled={true} pageSize={30} />

                <Column
                  dataField='Repd_NombreEstudio'
                  caption='Estudio Externo'
                  cellRender={renderEstudioExterno}
                  minWidth={160}
                  calculateCellValue={(rowData: any) => {
                    return `${rowData?.Repd_NombreEstudio || ''} ${rowData?.Repd_CodigoEstudio || ''} ${rowData?.Repd_IdEstudio || ''}`.trim();
                  }}
                />
                <Column
                  dataField='Estudio.Es_Nombre'
                  caption='Estudio Local'
                  cellRender={renderEstudioLocal}
                  minWidth={160}
                  calculateCellValue={(rowData: any) => {
                    const e = rowData?.Estudio;
                    return e ? `${e.Es_Nombre || ''} ${e.Es_Codigo || ''}`.trim() : '';
                  }}
                />
                <Column dataField='Repd_Es_Id' caption='Homologado' cellRender={renderHomologacion} alignment='center' width={105} />
                <Column dataField='Tarifa.Tf_Valor' caption='Tarifa' cellRender={renderTarifa} alignment='right' width={90} />
                <Column caption='Indic.' cellRender={renderIndicadores} alignment='center' width={95} allowSorting={false} allowFiltering={false} />
                <Column type='buttons' width={40} caption=''>
                  <Butt icon='trash' hint='Eliminar estudio' onClick={handleDeleteDetail} />
                </Column>
              </DataGrid>
            )}
            {/* Tooltips alertas estudios */}
            {details
              .filter((d) => d.Repd_Alertas)
              .map((d) => (
                <Tooltip key={`tip-det-${d.Repd_Id}`} target={`#alerta-det-${d.Repd_Id}`} showEvent='mouseenter' hideEvent='mouseleave' position='top'>
                  <div style={{ padding: '4px', whiteSpace: 'pre-line', maxWidth: 320 }}>{d.Repd_Alertas}</div>
                </Tooltip>
              ))}
            {/* Tooltips notas estudios */}
            {details
              .filter((d) => d.Repd_Notas && String(d.Repd_Notas).trim())
              .map((d) => (
                <Tooltip key={`tip-nota-${d.Repd_Id}`} target={`#nota-det-${d.Repd_Id}`} showEvent='mouseenter' hideEvent='mouseleave' position='top'>
                  <div style={{ padding: '4px', whiteSpace: 'pre-line', maxWidth: 320 }}>{d.Repd_Notas}</div>
                </Tooltip>
              ))}
            {/* Tooltips urgente estudios */}
            {details
              .filter((d) => d.Repd_Urgente)
              .map((d) => (
                <Tooltip key={`tip-urg-${d.Repd_Id}`} target={`#urg-det-${d.Repd_Id}`} showEvent='mouseenter' hideEvent='mouseleave' position='top'>
                  Urgente
                </Tooltip>
              ))}
          </div>
        </div>

        {/* Footer */}
        <div className='modal-ext-footer'>
          <div className='footer-info'>
            {selectedPatient && (
              <span>
                Paciente: <strong>{selectedPatient.Rep_Documento}</strong> — {details.length} estudio(s)
              </span>
            )}
          </div>
          <div style={{ display: 'flex', gap: '8px' }}>
            <Button text='Cerrar' type='default' stylingMode='contained' onClick={handleClose} />
          </div>
        </div>
      </div>

      {/* Popup de vincular paciente */}
      {linkingPatient && (
        <Popup visible={!!linkingPatient} onHiding={handleCloseLinkPatient} showCloseButton={true} showTitle={true} title={`Vincular Paciente — ${linkingPatient.Rep_Documento}`} width={isSmall ? '95%' : 460} height='auto' dragEnabled={true}>
          <div className='link-patient-popup'>
            <div className='link-patient-context'>
              <span className='link-patient-context-label'>Documento externo:</span>
              <strong>{linkingPatient.Rep_Documento}</strong>
              <span className='link-patient-context-label' style={{ marginLeft: '12px' }}>
                Orden:
              </span>
              <strong>{linkingPatient.Rep_Orden}</strong>
            </div>

            <form onSubmit={handleSearchPatient}>
              <div className='link-patient-search'>
                <div style={{ flex: 1 }}>
                  <TextBox stylingMode='outlined' label='Identificación del paciente' labelMode='static' value={searchDocument} onValueChange={setSearchDocument} disabled={searchLoading || linkLoading} />
                </div>
                <Button text='Buscar' type='default' stylingMode='contained' useSubmitBehavior={true} disabled={searchLoading || linkLoading || !searchDocument.trim()} />
              </div>
            </form>

            {searchLoading && (
              <div className='panel-loading' style={{ height: 60 }}>
                <LoadIndicator visible={true} height={24} width={24} />
                <span>Buscando paciente...</span>
              </div>
            )}

            {foundPatient && !searchLoading && (
              <div className='link-patient-result'>
                <div className='link-patient-result-info'>
                  <div className='link-patient-result-name'>
                    {foundPatient.Paciente?.Pc_Nombre || ''} {foundPatient.Paciente?.Pc_Apellido1 || ''}
                  </div>
                  <div className='link-patient-result-doc'>Doc: {foundPatient.Paciente?.Pc_Identificacion || '—'}</div>
                </div>
                <Button text='Vincular' type='success' stylingMode='contained' onClick={handleLinkFoundPatient} disabled={linkLoading} />
              </div>
            )}

            {linkLoading && (
              <div className='panel-loading' style={{ height: 60 }}>
                <LoadIndicator visible={true} height={24} width={24} />
                <span>Vinculando paciente...</span>
              </div>
            )}
          </div>
        </Popup>
      )}

      {/* Popup de edición de estudio local */}
      {editingDetail && (
        <Popup visible={!!editingDetail} onHiding={handleCloseEdit} showCloseButton={true} showTitle={true} title='Seleccionar estudio local' width={isSmall ? '95%' : 520} height={isSmall ? '80%' : 480} dragEnabled={true}>
          <div className='edit-study-popup'>
            <div className='edit-study-context'>
              <div className='edit-study-context-label'>Estudio externo:</div>
              <div className='edit-study-context-value'>
                <strong>{editingDetail.Repd_NombreEstudio}</strong>
                <span className='edit-study-context-code'>Cod: {editingDetail.Repd_CodigoEstudio}</span>
              </div>
            </div>

            <div className='edit-study-search'>
              <input type='text' className='edit-study-search-input' placeholder='Buscar estudio por nombre o código...' value={editSearchText} onChange={(e) => setEditSearchText(e.target.value)} autoFocus />
            </div>

            {savingDetail && (
              <div className='panel-loading' style={{ height: 60 }}>
                <LoadIndicator visible={true} height={24} width={24} />
                <span>Guardando...</span>
              </div>
            )}

            <div className='edit-study-list'>
              {filteredTarifas.length === 0 ? (
                <div className='edit-study-empty'>{editSearchText.length < 2 ? 'Escriba al menos 2 caracteres para buscar' : 'No se encontraron estudios'}</div>
              ) : (
                filteredTarifas.map((tarifa) => {
                  const isCurrentStudy = editingDetail.Repd_Es_Id === tarifa.Tf_Es_Id;
                  return (
                    <div key={tarifa.Tf_Id || tarifa.Tf_Es_Id} className={`edit-study-item ${isCurrentStudy ? 'edit-study-item-current' : ''}`} onClick={() => !savingDetail && !isCurrentStudy && handleSelectTarifa(tarifa)}>
                      <div className='edit-study-item-info'>
                        <span className='edit-study-item-name'>{tarifa.Estudio?.Es_Nombre || '—'}</span>
                        <span className='edit-study-item-code'>
                          Cod: {tarifa.Estudio?.Es_Codigo || '—'}
                          {tarifa.Estudio?.SysA_AreasAnalisis?.Aa_Nombre && ` | ${tarifa.Estudio.SysA_AreasAnalisis.Aa_Nombre}`}
                        </span>
                      </div>
                      <div className='edit-study-item-price'>${tarifa.Tf_Valor?.toLocaleString('es-CO') || '0'}</div>
                      {isCurrentStudy && (
                        <span className='chip chip-ok' style={{ marginLeft: 6 }}>
                          Actual
                        </span>
                      )}
                    </div>
                  );
                })
              )}
            </div>
          </div>
        </Popup>
      )}

      {/* Popup de Monitoreo */}
      <Popup visible={monitoreoVisible} onHiding={() => setMonitoreoVisible(false)} showCloseButton={true} showTitle={true} title={`Monitoreo — Remisión #${remisionId}`} fullScreen={isSmall} width={isSmall ? '100%' : '70%'} height={isSmall ? '100%' : '70%'} dragEnabled={true}>
        <div style={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
          {loadingMonitoreo ? (
            <div className='panel-loading' style={{ flex: 1 }}>
              <LoadIndicator visible={true} height={30} width={30} />
              <span>Cargando monitoreo...</span>
            </div>
          ) : monitoreoData.length === 0 ? (
            <div className='panel-empty' style={{ flex: 1 }}>
              <h5>Sin registros</h5>
              <p>No se encontró información de monitoreo para esta remisión.</p>
            </div>
          ) : (
            <DataGrid dataSource={monitoreoData} showBorders={true} rowAlternationEnabled={true} hoverStateEnabled={true} height='100%' showColumnLines={true} columnAutoWidth={true}>
              <SearchPanel visible={true} placeholder='Buscar...' width={200} />
              <Paging enabled={true} pageSize={20} />
              <FilterRow visible={true} />
              <Column dataField='Mtr_Fecha' caption='Fecha' width={160} alignment='center' calculateCellValue={(rowData: any) => (rowData.Mtr_Fecha ? moment(rowData.Mtr_Fecha).format('DD/MM/YYYY hh:mm a') : '—')} />
              <Column dataField='Usuario.Us_Nombre' caption='Usuario' />
              <Column dataField='Accion.Acm_Nombre' caption='Acción' width={180} />
              <Column dataField='Mtr_Descripcion' caption='Descripción' />
            </DataGrid>
          )}
        </div>
      </Popup>

      {/* Modales de confirmación */}
      <ModalConfirmation visible={confirmDeletePatientVisible} message='Desea eliminar este paciente de la remisión externa' onConfirm={handleConfirmDeletePatient} onCancel={handleCancelDeletePatient} />
      <ModalConfirmation visible={confirmDeleteDetailVisible} message='Desea eliminar este estudio del paciente' onConfirm={handleConfirmDeleteDetail} onCancel={handleCancelDeleteDetail} />
      <ModalConfirmation visible={confirmImportVisible} message='¿Desea confirmar e importar esta remisión externa?' onConfirm={handleConfirmImport} onCancel={() => setConfirmImportVisible(false)} />

      {/* Spinner mientras se procesa la remisión */}
      <LoadPanel visible={importingRemision} message='Procesando remisión...' showPane={true} shading={true} shadingColor='rgba(0, 0, 0, 0.4)' />
    </Popup>
  );
};
